import React from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <div className="legal-page">
      <nav className="legal-nav">
        <Link to="/" className="back-link">← Back to Home</Link>
      </nav>
      <div className="legal-container">
        <h1>Privacy Policy</h1>
        <p className="last-updated">Last Updated: September 7, 2026</p>

        <div className="highlight">
          <p><strong>Your privacy is important to us.</strong> This Privacy Policy explains how PriceDiary ("we," "our," or "us") collects, uses, discloses, and protects your information when you use our mobile application (the "App").</p>
        </div>

        <h2>1. Introduction</h2>
        <p>PriceDiary is a price tracking and comparison application that helps users track product prices, compare prices across different stores, scan receipts, and share price information with the community. We are committed to protecting your privacy and handling your personal information responsibly.</p>
        <p>By using PriceDiary, you agree to the collection and use of information in accordance with this Privacy Policy.</p>

        <h2>2. Information We Collect</h2>
        
        <h3>2.1 Information You Provide</h3>
        <p>We collect information that you provide directly to us, including:</p>
        <ul>
          <li><strong>Account Information:</strong> Name, email address, password (encrypted), and profile information</li>
          <li><strong>Location Information:</strong> City and location preferences to provide location-based price comparisons</li>
          <li><strong>Purchase Data:</strong> Product purchases, prices, store information, and receipt data (when you scan receipts)</li>
          <li><strong>User Content:</strong> Price information you share, reviews, comments, and other content you submit</li>
          <li><strong>Preferences:</strong> App settings, notification preferences, and sharing preferences</li>
        </ul>

        <h3>2.2 Information Collected Automatically</h3>
        <p>When you use the App, we automatically collect certain information, including:</p>
        <ul>
          <li><strong>Device Information:</strong> Device type, operating system, unique device identifiers, and mobile network information</li>
          <li><strong>Usage Data:</strong> How you interact with the App, features used, time spent, and pages viewed</li>
          <li><strong>Location Data:</strong> Precise location data (with your permission) to provide location-based services</li>
          <li><strong>Log Data:</strong> IP address, access times, app crashes, and performance data</li>
        </ul>

        <h3>2.3 Information from Third Parties</h3>
        <p>We may receive information from third-party services you use to sign in, such as:</p>
        <ul>
          <li><strong>Google Sign-In:</strong> If you sign in with Google, we receive your name, email address, and profile picture</li>
          <li><strong>Firebase:</strong> Authentication and user data stored securely in Firebase</li>
          <li><strong>Google Places API:</strong> Location and place information for store finder features</li>
        </ul>

        <h2>3. How We Use Your Information</h2>
        <p>We use the information we collect to:</p>
        <ul>
          <li>Provide, maintain, and improve the App and its features</li>
          <li>Create and manage your account</li>
          <li>Process and store your purchase history and price tracking data</li>
          <li>Provide location-based price comparisons and store recommendations</li>
          <li>Enable community features, including sharing price information (with your consent)</li>
          <li>Send you notifications about price changes, reminders, and app updates (with your consent)</li>
          <li>Analyze usage patterns to improve the App's functionality and user experience</li>
          <li>Detect, prevent, and address technical issues and security threats</li>
          <li>Comply with legal obligations and enforce our Terms of Service</li>
          <li>Respond to your inquiries and provide customer support</li>
        </ul>

        <h2>4. How We Share Your Information</h2>
        
        <h3>4.1 Public Information</h3>
        <p>With your consent and based on your privacy settings, certain information may be shared publicly:</p>
        <ul>
          <li>Price information you choose to share with the community</li>
          <li>Store recommendations and price comparisons (anonymized or aggregated)</li>
        </ul>

        <h3>4.2 Service Providers</h3>
        <p>We may share your information with third-party service providers who perform services on our behalf, including:</p>
        <ul>
          <li><strong>Firebase (Google):</strong> For data storage, authentication, and analytics</li>
          <li><strong>Google Cloud Services:</strong> For hosting and infrastructure</li>
          <li><strong>Analytics Providers:</strong> To understand app usage and improve our services</li>
          <li><strong>Customer Support:</strong> To provide technical support and respond to inquiries</li>
        </ul>
        <p>These service providers are contractually obligated to protect your information and use it only for the purposes we specify.</p>

        <h3>4.3 Legal Requirements</h3>
        <p>We may disclose your information if required by law or in response to valid requests by public authorities (e.g., court orders, subpoenas).</p>

        <h3>4.4 Business Transfers</h3>
        <p>In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity.</p>

        <h3>4.5 With Your Consent</h3>
        <p>We may share your information with third parties when you explicitly consent to such sharing.</p>

        <h2>5. Data Security</h2>
        <p>We implement appropriate technical and organizational measures to protect your personal information, including:</p>
        <ul>
          <li>Encryption of data in transit and at rest</li>
          <li>Secure authentication and access controls</li>
          <li>Regular security assessments and updates</li>
          <li>Limited access to personal information on a need-to-know basis</li>
        </ul>
        <p>However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.</p>

        <h2>6. Your Rights and Choices</h2>
        
        <h3>6.1 Access and Update</h3>
        <p>You can access and update your account information at any time through the App's settings.</p>

        <h3>6.2 Data Deletion</h3>
        <p>You can request deletion of your account and associated data by contacting us at <a href="mailto:support@logicalflows.com">support@logicalflows.com</a> or through the App settings.</p>

        <h3>6.3 Location Services</h3>
        <p>You can control location permissions through your device settings. Disabling location services may limit certain features of the App.</p>

        <h3>6.4 Notifications</h3>
        <p>You can manage notification preferences through the App settings or your device settings.</p>

        <h3>6.5 Sharing Preferences</h3>
        <p>You can control whether to share price information with the community through your privacy settings in the App.</p>

        <h3>6.6 Data Portability</h3>
        <p>You may request a copy of your personal data in a structured, machine-readable format.</p>

        <h2>7. Children's Privacy</h2>
        <p>PriceDiary is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If we become aware that we have collected information from a child under 13, we will take steps to delete such information promptly.</p>
        <p>If you are a parent or guardian and believe your child has provided us with personal information, please contact us at <a href="mailto:support@logicalflows.com">support@logicalflows.com</a>.</p>

        <h2>8. International Data Transfers</h2>
        <p>Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws that differ from those in your country. By using the App, you consent to the transfer of your information to these countries.</p>

        <h2>9. Data Retention</h2>
        <p>We retain your personal information for as long as necessary to provide the App and fulfill the purposes described in this Privacy Policy, unless a longer retention period is required or permitted by law. When you delete your account, we will delete or anonymize your personal information, subject to legal retention requirements.</p>

        <h2>10. Third-Party Services</h2>
        <p>The App may contain links to or integrate with third-party services. This Privacy Policy does not apply to third-party services. We encourage you to review the privacy policies of third-party services you use, including:</p>
        <ul>
          <li><strong>Google:</strong> <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a></li>
          <li><strong>Firebase:</strong> <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer">Firebase Privacy Policy</a></li>
        </ul>

        <h2>11. California Privacy Rights</h2>
        <p>If you are a California resident, you have certain rights under the California Consumer Privacy Act (CCPA), including:</p>
        <ul>
          <li>The right to know what personal information we collect, use, and disclose</li>
          <li>The right to delete your personal information</li>
          <li>The right to opt-out of the sale of personal information (we do not sell personal information)</li>
          <li>The right to non-discrimination for exercising your privacy rights</li>
        </ul>
        <p>To exercise these rights, please contact us at <a href="mailto:support@logicalflows.com">support@logicalflows.com</a>.</p>

        <h2>12. European Privacy Rights (GDPR)</h2>
        <p>If you are located in the European Economic Area (EEA), you have certain rights under the General Data Protection Regulation (GDPR), including:</p>
        <ul>
          <li>The right to access your personal data</li>
          <li>The right to rectify inaccurate data</li>
          <li>The right to erasure ("right to be forgotten")</li>
          <li>The right to restrict processing</li>
          <li>The right to data portability</li>
          <li>The right to object to processing</li>
          <li>The right to withdraw consent</li>
        </ul>
        <p>To exercise these rights, please contact us at <a href="mailto:support@logicalflows.com">support@logicalflows.com</a>.</p>

        <h2>13. Changes to This Privacy Policy</h2>
        <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by:</p>
        <ul>
          <li>Updating the "Last Updated" date at the top of this page</li>
          <li>Posting a notice in the App</li>
          <li>Sending you an email notification (if you have provided an email address)</li>
        </ul>
        <p>Your continued use of the App after changes become effective constitutes acceptance of the updated Privacy Policy.</p>

        <h2>14. Cookies and Tracking Technologies</h2>
        <p>The App may use cookies and similar tracking technologies to enhance your experience, analyze usage, and assist with marketing efforts. You can control cookie preferences through your device settings, though this may affect App functionality.</p>

        <h2>15. Contact Us</h2>
        <div className="contact-info">
          <p><strong>If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:</strong></p>
          <p><strong>Email:</strong> <a href="mailto:support@logicalflows.com">support@logicalflows.com</a></p>
          <p><strong>Website:</strong> <a href="https://logicalflows.com/" target="_blank" rel="noopener noreferrer">https://logicalflows.com/</a></p>
          <p><strong>Data Protection Officer:</strong> <a href="mailto:support@logicalflows.com">support@logicalflows.com</a></p>
        </div>

        <h2>16. Consent</h2>
        <p>By using PriceDiary, you consent to our Privacy Policy and agree to its terms. If you do not agree with this Privacy Policy, please do not use the App.</p>

        <p style={{ marginTop: '30px', textAlign: 'center', color: '#7f8c8d' }}>
          © 2026 PriceDiary / Logical Flows. All rights reserved.
        </p>
      </div>
    </div>
  );
};

export default PrivacyPolicy;

